import type {
  ContractParty,
  ContractPartyEntityType,
  ContractPartyPosition,
  ContractPartyRole,
} from "@closer/shared";
import {
  CONTRACT_PARTY_ENTITY_TYPE_NAMES,
  CONTRACT_PARTY_ENTITY_TYPES,
  CONTRACT_PARTY_POSITION_HINTS,
  CONTRACT_PARTY_POSITION_NAMES,
  CONTRACT_PARTY_ROLE_NAMES,
  CONTRACT_PARTY_ROLES,
  positionFromLegacyType,
  SECILEBILIR_PARTY_POSITIONS,
} from "@closer/shared";
import { Button, Checkbox, FormField, Input, Modal, Select } from "@closer/ui";
import { useEffect, useState } from "react";

/**
 * TARAF PENCERESİ — KONUM × TÜR (CLOS-1124 / CLOS-1151).
 *
 * ### ⚠ Eski "Taraf türü" (workspace/counterparty/client/...) seçimi KALKTI
 *
 * O alan üç soruyu birden soruyordu; kullanıcı "müvekkil mi, karşı taraf mı?" diye
 * ikilemde kalıyordu — ikisi de olabilir. Şimdi iki net soru var: HANGİ SAFTA (bizim
 * taraf / bizimle aynı safta / karşı taraf) ve NE (gerçek kişi / kuruluş). `partyType`
 * sunucuda türetilir; istemci göndermez.
 *
 * ### ⚠ Seçim ZORUNLU, varsayılan YOK
 *
 * Konum ve tür boş açılır; "Ekle" ikisi seçilmeden açılmaz. Varsayılan "karşı taraf ·
 * kuruluş" koysaydık, elle girilen her taraf sessizce o etiketi alırdı — tam da 0192'nin
 * eski veride tahmin etmeyi yasakladığı şey (Halit kararı 3). Eski satırı düzenlerken
 * konum türden türetilir (deterministik), tür ise KULLANICIDAN sorulur.
 */
type Alanlar = {
  position: ContractPartyPosition | "";
  entityType: ContractPartyEntityType | "";
  role: ContractPartyRole;
  displayName: string;
  registryNumber: string;
  email: string;
  phone: string;
  contactName: string;
};

const BOS: Alanlar = {
  position: "",
  entityType: "",
  role: CONTRACT_PARTY_ROLES[0],
  displayName: "",
  registryNumber: "",
  email: "",
  phone: "",
  contactName: "",
};

function alanlardan(taraf: ContractParty): Alanlar {
  return {
    position: taraf.position ?? positionFromLegacyType(taraf.partyType) ?? "",
    entityType: taraf.entityType ?? "",
    role: taraf.role,
    displayName: taraf.displayName,
    registryNumber: taraf.registryNumber ?? "",
    email: taraf.email ?? "",
    phone: taraf.phone ?? "",
    contactName: taraf.contactName ?? "",
  };
}

export function ContractPartyDialog({
  open,
  onClose,
  onConfirm,
  onConfirmAndInvite,
  davetEdebilir = false,
  presetler = [],
  isPending,
  mevcut = null,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: (girdi: Record<string, unknown>) => void;
  /**
   * "TARAFI EKLE VE DAVET GÖNDER" (CLOS-1125 · 1156, rapor §5.1.2): taraf + davet tek adımda.
   * Kullanıcı ayrı organizasyon/katılımcı/yetki ekranlarına taşınmaz; kapsam preset ile.
   */
  onConfirmAndInvite?: (
    girdi: Record<string, unknown>,
    kapsam: { presetId?: string; permissions?: string[] },
  ) => void;
  davetEdebilir?: boolean;
  /** Dış kapsam presetleri (ebeveyn getirir; pencere saf kalır, test sağlayıcı istemez). */
  presetler?: readonly { id: string; name: string }[];
  isPending: boolean;
  /** Doluysa düzenleme modu: alanlar bu taraftan beslenir, düğme "Kaydet" olur. */
  mevcut?: ContractParty | null;
}) {
  const [deger, setDeger] = useState<Alanlar>(BOS);
  const [davetle, setDavetle] = useState(false);
  const [preset, setPreset] = useState("");

  useEffect(() => {
    if (open) {
      setDeger(mevcut ? alanlardan(mevcut) : BOS);
      setDavetle(false);
      setPreset("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- taslak yalnız AÇILIŞTA beslenir
  }, [open]);

  const turEksik = mevcut !== null && mevcut.entityType === null;
  const gonderilebilir =
    deger.displayName.trim() !== "" && deger.position !== "" && deger.entityType !== "";

  function gonder() {
    if (!gonderilebilir) return;
    const govde: Record<string, unknown> = {
      position: deger.position,
      entityType: deger.entityType,
      role: deger.role,
      displayName: deger.displayName.trim(),
    };
    for (const alan of ["registryNumber", "email", "phone", "contactName"] as const) {
      const ham = deger[alan].trim();
      if (ham.length > 0) govde[alan] = ham;
    }
    if (davetle && onConfirmAndInvite) {
      onConfirmAndInvite(
        govde,
        preset === ""
          ? { permissions: ["contract.view", "contract.revision"] }
          : { presetId: preset },
      );
      return;
    }
    onConfirm(govde);
  }
  /** Davet için adres şart; kendi tarafımız (workspace) davet edilmez. */
  const davetSecilebilir =
    davetEdebilir &&
    mevcut === null &&
    onConfirmAndInvite !== undefined &&
    deger.position !== "workspace";
  const davetGonderilebilir = !davetle || deger.email.trim() !== "";

  const yaz = (alan: keyof Alanlar) => (e: { target: { value: string } }) =>
    setDeger((d) => ({ ...d, [alan]: e.target.value }));

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={mevcut ? "Tarafı düzenle" : "Taraf ekle"}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Vazgeç
          </Button>
          <Button
            onClick={gonder}
            loading={isPending}
            disabled={!gonderilebilir || !davetGonderilebilir}
          >
            {mevcut ? "Kaydet" : davetle ? "Tarafı ekle ve davet gönder" : "Ekle"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <FormField label="Taraf adı" required>
          <Input aria-label="Taraf adı" value={deger.displayName} onChange={yaz("displayName")} />
        </FormField>
        {/*
          ⚠ SEÇENEKLER YAZMA LİSTESİNDEN (CLOS-1267).

          Liste eskiden `CONTRACT_PARTY_POSITIONS`'ın tamamından üretiliyordu ve kanonun
          yeni yazımda YASAKLADIĞI `workspace` değeri kullanıcıya sunuluyordu. Eski
          satırlar hâlâ o değeri taşıyabiliyor ve kartta okunuyor; seçenek olarak
          sunulmuyor.

          ⚠ AÇIKLAMA SEÇİLİ KONUMUN ALTINDA: etiketler tek başına ayırt etmiyordu
          ("Temsil edilen taraf" ile "Bizim tarafımız" aynı cümle gibi okunuyordu).
        */}
        <FormField label="Taraf konumu" required>
          <Select aria-label="Taraf konumu" value={deger.position} onChange={yaz("position")}>
            <option value="">Seçiniz</option>
            {SECILEBILIR_PARTY_POSITIONS.map((k) => (
              <option key={k} value={k}>
                {CONTRACT_PARTY_POSITION_NAMES[k]}
              </option>
            ))}
          </Select>
          {deger.position ? (
            <p className="mt-1 text-xs text-fg-faint" data-testid="konum-aciklamasi">
              {CONTRACT_PARTY_POSITION_HINTS[deger.position]}
            </p>
          ) : null}
        </FormField>
        <FormField
          label="Taraf türü"
          required
          hint={
            turEksik
              ? "Bu tarafın türü belirtilmemiş; kaydetmek için gerçek kişi ya da kuruluş seçin."
              : undefined
          }
        >
          <Select aria-label="Taraf türü" value={deger.entityType} onChange={yaz("entityType")}>
            <option value="">Seçiniz</option>
            {CONTRACT_PARTY_ENTITY_TYPES.map((t) => (
              <option key={t} value={t}>
                {CONTRACT_PARTY_ENTITY_TYPE_NAMES[t]}
              </option>
            ))}
          </Select>
        </FormField>
        <FormField label="Rol" required>
          <Select aria-label="Rol" value={deger.role} onChange={yaz("role")}>
            {CONTRACT_PARTY_ROLES.map((r) => (
              <option key={r} value={r}>
                {CONTRACT_PARTY_ROLE_NAMES[r]}
              </option>
            ))}
          </Select>
        </FormField>
        <FormField label="Kimlik / vergi numarası">
          <Input
            aria-label="Kimlik / vergi numarası"
            value={deger.registryNumber}
            onChange={yaz("registryNumber")}
          />
        </FormField>
        <FormField label="E-posta">
          <Input aria-label="E-posta" type="email" value={deger.email} onChange={yaz("email")} />
        </FormField>
        <FormField label="Telefon">
          <Input aria-label="Telefon" value={deger.phone} onChange={yaz("phone")} />
        </FormField>
        <FormField label="İlgili kişi">
          <Input aria-label="İlgili kişi" value={deger.contactName} onChange={yaz("contactName")} />
        </FormField>
        {davetSecilebilir ? (
          <div className="flex flex-col gap-2 rounded-md bg-muted p-2">
            {/* Kit `Checkbox` etiketi kendisi bağlar (uiGuards Bekçi 3: sayfada ham form elementi yok). */}
            <Checkbox
              label="Ortak çalışmaya davet gönder"
              className="text-fg"
              checked={davetle}
              onChange={(e) => setDavetle(e.target.checked)}
            />
            {davetle ? (
              <>
                {/*
                  ⚠ DAVET ERİŞİM DEĞİL: kabul edilene kadar hiçbir şey açılmaz. Kurum tarafında
                  hangi çalışanın erişeceğini kabul eden kurumun yöneticisi kendi belirler
                  (rapor §5.1.1); burada yalnız DIŞ KAPSAM seçilir — checkbox matrisi yok.
                */}
                <p className="text-xs text-fg-soft">
                  Davet tarafın e-postasına gider; erişim kabulle başlar. Seçtiğiniz kapsam karşı
                  tarafın bu sözleşmede aşamayacağı sınırdır; kurumsa kendi çalışanlarına erişimi
                  kendisi dağıtır.
                </p>
                <FormField label="Erişim kapsamı">
                  <Select
                    aria-label="Erişim kapsamı"
                    value={preset}
                    onChange={(e) => setPreset(e.target.value)}
                  >
                    <option value="">Görüntüleme + Revizyon önerme (varsayılan)</option>
                    {presetler.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </Select>
                </FormField>
                {deger.email.trim() === "" ? (
                  <span className="text-xs text-warning">Davet için e-posta adresi gerekli.</span>
                ) : null}
              </>
            ) : null}
          </div>
        ) : null}
      </div>
    </Modal>
  );
}
